import { ButtonGroup, IconButton, makeStyles, Paper, Typography } from '@material-ui/core'
import { useParams } from 'react-router-dom'
import { MountpointStat, useMountpointInfo, useMountpointStat } from '../api'
import { FC, useEffect, useRef, useState } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { sub } from 'date-fns'
import { SyncRounded } from '@material-ui/icons'
import { formatDuration } from '../format-helpers'
import _ from 'lodash'
import Card from 'common/Card'
import { ToggleButton } from '../ToggleButton'
import { fullscreenExitIcon, fullscreenIcon } from './MountpointSatellitesHistory/constants'
import { strings } from '../strings'
import { generateExportFileName } from 'hooks'

export function MountpointStats() {
  const classes = useMountpointStatsStyles()

  const { mid } = useParams<any>()
  const { mountpointStat, error, refresh } = useMountpointStat(mid)

  if (error) return <Paper>{error.toString()}</Paper>
  if (!mountpointStat) return <Paper>Loading</Paper>

  return (
    <div className={classes.root}>
      <CurrentStatus stat={mountpointStat} onSync={() => refresh()} />
      <RtcmChart stat={mountpointStat} mid={mid} />
    </div>
  )
}

const useMountpointStatsStyles = makeStyles({
  root: {
    height: '100%',
    width: '100%',
    // overflowY: 'auto',
  },
})

const CurrentStatus: FC<{ stat: MountpointStat; onSync: () => void }> = ({ stat, onSync }) => {
  const classes = useCurrentStatusStyles()

  const lastEventTime =
    stat.eventLog && stat.eventLog.length > 0 ? stat.eventLog[stat.eventLog.length - 1][0] : null

  return (
    <Card title={strings.messagesCountGraph}>
      <IconButton className={classes.sync} onClick={onSync}>
        <SyncRounded />
      </IconButton>
      <table className={classes.table}>
        <colgroup>
          <col className={classes.keyColumn} />
          <col className={classes.valueColumn} />
        </colgroup>
        <tbody>
          <tr>
            <td>
              <Typography>{strings.connectedTo}:</Typography>
            </td>
            <td>
              <Typography>{stat.remote || strings.disconnected}</Typography>
            </td>
          </tr>
          <tr>
            <td>
              <Typography>
                {stat.since ? strings.connectedTime : strings.disconnectedTime}:
              </Typography>
            </td>
            <td>
              <Typography>
                {lastEventTime &&
                  formatDuration(
                    Math.round(new Date().getTime() / 1000) - (stat.since || lastEventTime),
                  )}
              </Typography>
            </td>
          </tr>
          <tr>
            <td>
              <Typography>{strings.sourceAgent}:</Typography>
            </td>
            <td>
              <Typography>{stat.sourceAgent || strings.empty}</Typography>
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  )
}

const useCurrentStatusStyles = makeStyles((theme) => ({
  table: {
    borderCollapse: 'separate',
    borderSpacing: theme.spacing(1, 1),
    '& td': {
      padding: theme.spacing(0.5, 1),
    },
  },
  keyColumn: {
    backgroundColor: '#e9e9e9',
    borderRadius: 4,
  },
  valueColumn: {},
  sync: {
    float: 'right',
  },
}))

const RtcmChart: FC<{ stat: MountpointStat; mid: any }> = ({ stat, mid }) => {
  const classes = useRtcmChartStyles()
  const [timeRange, setTimeRange] = useState('today')
  const chartDivRef = useRef<HTMLDivElement | null>(null)
  const [fullScreen, setFullScreen] = useState(false)

  const { mountpointInfo } = useMountpointInfo(mid)

  useEffect(() => {
    if (chartDivRef == null || !stat || !mountpointInfo) return
    const chart = new ChartController(
      chartDivRef.current!!,
      timeRange,
      stat,
      fullScreen,
      setFullScreen,
      mountpointInfo.name,
    )
    return () => chart.destroy()
  }, [timeRange, stat, fullScreen, mountpointInfo])

  return (
    <Card fullScreen={fullScreen} style={{ marginTop: fullScreen ? 0 : 3 }}>
      <div className={classes.title}>
        <Typography>{strings.mountpointChartTitle}</Typography>
        <ButtonGroup disableElevation variant="outlined" size="small">
          <ToggleButton selected={timeRange === 'today'} onClick={() => setTimeRange('today')}>
            {strings.today}
          </ToggleButton>
          <ToggleButton selected={timeRange === 'month'} onClick={() => setTimeRange('month')}>
            {strings.month}
          </ToggleButton>
          <ToggleButton selected={timeRange === 'allTime'} onClick={() => setTimeRange('allTime')}>
            {strings.allTime}
          </ToggleButton>
        </ButtonGroup>
      </div>
      <div style={{ direction: 'ltr', height: fullScreen ? '100%' : 300 }} ref={chartDivRef} />
    </Card>
  )
}

const useRtcmChartStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
  },
  paperBig: {
    padding: theme.spacing(2),
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1,
  },
  chartWrapper: {
    height: 300,
    overflow: 'hidden',
    paddingBottom: theme.spacing(1),
  },
  chartWrapperBig: {
    flexGrow: 1,
    overflow: 'hidden',
  },
  title: {
    flip: false,
    margin: theme.spacing(0, 2, 2, 2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  resizeIcon: {
    flip: false,
    position: 'absolute',
    top: -theme.spacing(1),
    left: -theme.spacing(1),
  },
  resizeIconBig: {
    flip: false,
    position: 'absolute',
    top: 0,
    left: 0,
    transform: 'rotate(180deg)',
  },
}))

class ChartController {
  chart: ApexCharts
  disconnectDurations: { disconnect: number; connect: number }[]
  rtcmLogs: number[][]
  annotations: boolean

  constructor(
    div: HTMLDivElement,
    timeRange: string,
    stat: MountpointStat,
    annotations: boolean,
    setFullScreen: React.Dispatch<React.SetStateAction<boolean>>,
    mountpointName: string,
  ) {
    this.annotations = annotations

    if (timeRange === 'today') {
      this.rtcmLogs = stat.dayLog || []
    } else if (timeRange === 'month') {
      const from = sub(new Date(), { months: 1 }).getTime() / 1000
      this.rtcmLogs = this.fillMissing((stat.hourlyLog || []).filter((x) => x[0] > from))
    } else {
      this.rtcmLogs = this.fillMissing(stat.hourlyLog || [])
    }

    this.disconnectDurations = []

    if (this.rtcmLogs.length > 0) {
      let needConnect = false
      for (const log of stat.eventLog || []) {
        if (!needConnect && log[1] === 2) {
          this.disconnectDurations.push({ disconnect: log[0], connect: 0 })
          needConnect = true
          continue
        }
        if (needConnect && log[1] === 1) {
          this.disconnectDurations[this.disconnectDurations.length - 1].connect = log[0]
          needConnect = false
        }
      }

      this.disconnectDurations = this.disconnectDurations.filter(
        (x) =>
          x.disconnect > this.rtcmLogs[0][0] &&
          x.connect < this.rtcmLogs[this.rtcmLogs.length - 1][0],
      )
    }

    this.chart = new ApexCharts(
      div,
      this.getOptions(timeRange, stat, annotations, setFullScreen, mountpointName),
    )
    this.chart.render().then(() => this.updateAnnotations(0))
  }

  totalXRange(): number {
    return this.rtcmLogs.length > 0
      ? this.rtcmLogs[this.rtcmLogs.length - 1][0] - this.rtcmLogs[0][0]
      : 0
  }

  updateAnnotations(xAxixRange: number) {
    if (!this.annotations) return

    xAxixRange = xAxixRange || this.totalXRange()
    const stepSize = xAxixRange * 0.1
    const dds: { connect: number; disconnect: number; cluster: number; totalDis: number }[] = []

    for (const d of this.disconnectDurations) {
      if (dds.length === 0) {
        dds.push({
          ...d,
          cluster: 1,
          totalDis: d.connect - d.disconnect,
        })
        continue
      }
      const lastDD = dds[dds.length - 1]
      if (d.disconnect - lastDD.connect < stepSize) {
        lastDD.totalDis += d.connect - d.disconnect
        lastDD.connect = d.connect
        lastDD.cluster++
      } else {
        dds.push({
          ...d,
          cluster: 1,
          totalDis: d.connect - d.disconnect,
        })
      }
    }

    const xAxisAnnotations: XAxisAnnotations[] = []
    for (let d of dds) {
      let text = '∞'
      if (d.connect) {
        text = formatDuration(d.totalDis)
        if (d.cluster > 1) {
          const percentDis = (d.totalDis / (d.connect - d.disconnect)) * 100
          const eventsPerHour = d.cluster / ((d.connect - d.disconnect) / 3600)
          if (percentDis < 0.1 && eventsPerHour < 1) {
            continue
          }
          text += ' '
          text += _.round(percentDis, 1)
          text += '% '
          text += d.cluster
        }
      }
      xAxisAnnotations.push({
        x: d.disconnect * 1000,
        x2: (d.connect || d.disconnect) * 1000,
        fillColor: d.cluster > 1 ? '#fccc84' : '#ff9d9d',
        label: {
          borderColor: d.cluster > 1 ? '#ef9425' : '#ef2525',
          style: {
            fontSize: '14px',
            fontFamily: 'vazir',
            color: '#fff',
            background: d.cluster > 1 ? '#d78e11' : '#b30808',
          },
          text,
          orientation: 'horizontal',
          position: 'bottom',
          offsetY: -15,
        },
      } as XAxisAnnotations)
    }

    // noinspection JSIgnoredPromiseFromCall
    this.chart.updateOptions({ annotations: { xaxis: xAxisAnnotations } } as ApexOptions)
  }

  destroy() {
    this.chart.destroy()
  }

  getOptions(
    timeRange: string,
    stat: MountpointStat,
    annotations: boolean,
    setFullScreen: React.Dispatch<React.SetStateAction<boolean>>,
    mountpointName: string,
  ): ApexOptions {
    const controller = this
    return {
      series: [
        {
          name: '#RTCM',
          data: this.rtcmLogs.map((x) => [x[0] * 1000, x[1]]) as any,
        },
      ],
      chart: {
        height: annotations ? '92.5%' : 300,
        type: 'area',
        animations: {
          enabled: false,
        },
        events: {
          zoomed(chart: any, { xaxis }: any) {
            controller.updateAnnotations((xaxis.max - xaxis.min) / 1000)
          },
        },
        dropShadow: {
          enabled: false,
          top: 2,
          left: 0,
          blur: 2,
          opacity: 0.1,
        },
        toolbar: {
          tools: {
            customIcons: [
              {
                index: 6,
                icon: annotations ? fullscreenExitIcon : fullscreenIcon,
                title: 'fullscreenIcon',
                click: () => setFullScreen((prev) => !prev),
                class: 'apexcharts-zoom-icon fullscreenIcon',
              },
            ],
          },
          export: {
            csv: {
              filename: generateExportFileName(mountpointName, `rtcm_${timeRange}`),
            },
            svg: {
              filename: generateExportFileName(mountpointName, `rtcm_${timeRange}`),
            },
            png: {
              filename: generateExportFileName(mountpointName, `rtcm_${timeRange}`),
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        min: 0,
      },
      tooltip: {
        x: {
          format: 'yyyy/MM/dd HH:mm:ss',
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.5,
          opacityTo: 0.2,
          stops: [0, 20],
        },
      },
    }
  }

  fillMissing(logs: number[][]): number[][] {
    if (logs.length < 2) {
      return logs
    }

    const res: number[][] = []

    for (let log of logs) {
      if (res.length > 0) {
        const last = res[res.length - 1][0]
        const diff = log[0] - last
        // give 60 seconds grace period
        if (diff > 3600 + 60) {
          const missingCount = Math.floor(diff / 3600)
          for (let i = 0; i < missingCount; i++) {
            res.push([last + 3600 * (i + 1), null!])
          }
        }
      }
      res.push([...log])
    }

    return res
  }
}
